/**
 * Sign-off Pack Format Grouping Utility
 * 
 * Groups assignment rows for display in the operator sign-off sheet.
 * When pack assignments are detected, shows Campaign and Task as section headers
 * with Media Formats listed beneath them, instead of repeating on every row.
 * 
 * Safety Rules for Grouping:
 * 1. Only group when taskName explicitly indicates a pack (contains "Pack Installation") 
 *    OR when packId is present and not null
 * 2. When in doubt, fall back to flat display for that record set
 * 3. Log ambiguous cases in development only
 * 4. Maintain stable sorting within groups
 */

export interface Assignment {
  lockedAssignmentId: string | number;
  storeId: string | number;
  storeName: string;
  operatorId: string | number | null;
  operatorName: string;
  formatId: string | number | null;
  formatName: string | null;
  campaignId: string | number | null;
  campaignName: string | null;
  expectedQuantity: number;
  taskId: string | number | null;
  taskName: string | null;
  packId?: string | number | null;
  isPackAssignment?: boolean;
  deliveredQuantity?: number | null;
  comments?: string;
}

export interface PackGroupItem {
  assignmentId: string | number;
  mediaFormatName: string;
  expectedQty: number;
  deliveredQty?: number | null;
  comments?: string;
}

export interface PackGroup {
  type: 'pack';
  groupKey: string;
  campaignName: string;
  taskLabel: string;
  items: PackGroupItem[];
}

export interface SingleAssignment {
  type: 'single';
  assignmentId: string | number;
  campaignName: string;
  taskLabel: string;
  mediaFormatName: string;
  expectedQty: number;
  deliveredQty?: number | null;
  comments?: string;
}

export interface GroupingResult {
  groups: PackGroup[];
  singles: SingleAssignment[];
}

export interface GroupingOptions {
  enableDevWarnings?: boolean;
}

/**
 * Determines if an assignment should be grouped as a pack
 * Uses conservative rules to avoid false positives
 */
function shouldGroupAsPack(assignment: Assignment): boolean {
  const taskName = assignment.taskName || '';
  const packId = assignment.packId;
  const isPackAssignment = assignment.isPackAssignment;
  
  // Rule 1: Pack ID present and not null (primary indicator)
  if (packId !== null && packId !== undefined && packId !== '') {
    return true;
  }
  
  // Rule 2: Explicit pack assignment flag
  if (isPackAssignment === true) {
    return true;
  }
  
  // Rule 3: Task name explicitly indicates pack installation
  if (taskName.toLowerCase().includes('pack installation')) {
    return true;
  }
  
  // Rule 4: Campaign name suggests pack-based structure (temporary for testing)
  const campaignName = assignment.campaignName || '';
  if (campaignName.toLowerCase().includes('pack') && Number(assignment.taskId) === 1) {
    return true;
  }
  
  // Default to false for safety
  return false;
}

/**
 * Creates a stable group key for pack grouping
 */
function createGroupKey(assignment: Assignment): string {
  const campaignId = assignment.campaignId || 'no-campaign';
  const taskName = assignment.taskName || 'no-task';
  const packId = assignment.packId || 'no-pack';
  
  return `${campaignId}|${taskName}|${packId}`;
}

/**
 * Groups assignments for sign-off display
 * 
 * @param assignments - Raw assignment rows
 * @param opts - Grouping options
 * @returns Grouped and single assignments for display
 */
export function groupAssignmentsForSignoff(
  assignments: Assignment[], 
  opts: GroupingOptions = {}
): GroupingResult {
  const { enableDevWarnings = process.env.NODE_ENV !== 'production' } = opts;
  
  // Initialize result collections
  const packGroupMap = new Map<string, {
    campaignName: string;
    taskLabel: string;
    items: PackGroupItem[];
  }>();
  const singles: SingleAssignment[] = [];
  
  // Track ambiguous cases for logging
  const ambiguousCases: Assignment[] = [];
  
  // Sort assignments by operator first for stable grouping
  const sortedAssignments = assignments.sort((a, b) => {
    // Sort by operator ID first (treat null as high value)
    const aOperatorId = a.operatorId ?? 999999;
    const bOperatorId = b.operatorId ?? 999999;
    if (aOperatorId !== bOperatorId) {
      return String(aOperatorId).localeCompare(String(bOperatorId));
    }
    
    // Then by operator name for readability
    if (a.operatorName !== b.operatorName) {
      return a.operatorName.localeCompare(b.operatorName);
    }
    
    // Then by campaign name
    const aCampaign = a.campaignName || '';
    const bCampaign = b.campaignName || '';
    if (aCampaign !== bCampaign) {
      return aCampaign.localeCompare(bCampaign);
    }
    
    // Then by task name
    const aTask = a.taskName || '';
    const bTask = b.taskName || '';
    if (aTask !== bTask) {
      return aTask.localeCompare(bTask);
    }
    
    // Finally by store name for consistency
    return a.storeName.localeCompare(b.storeName);
  });

  // Process each assignment
  for (const assignment of sortedAssignments) {
    const shouldGroup = shouldGroupAsPack(assignment);
    
    // Check for ambiguous cases (has some pack indicators but not others)
    if (!shouldGroup) {
      const hasTaskIndicator = (assignment.taskName || '').toLowerCase().includes('pack');
      const hasPackId = assignment.packId !== null && assignment.packId !== undefined;
      
      if (hasTaskIndicator || hasPackId) {
        ambiguousCases.push(assignment);
      }
    }
    
    if (shouldGroup) {
      // Group this assignment
      const groupKey = createGroupKey(assignment);
      
      if (!packGroupMap.has(groupKey)) {
        packGroupMap.set(groupKey, {
          campaignName: assignment.campaignName || 'Unknown Campaign',
          taskLabel: assignment.taskName || 'Unknown Task',
          items: []
        });
      }
      
      const group = packGroupMap.get(groupKey)!;
      group.items.push({
        assignmentId: assignment.lockedAssignmentId,
        mediaFormatName: assignment.formatName || '—',
        expectedQty: assignment.expectedQuantity || 0,
        deliveredQty: assignment.deliveredQuantity,
        comments: assignment.comments
      });
    } else {
      // Keep as single assignment
      singles.push({
        type: 'single',
        assignmentId: assignment.lockedAssignmentId,
        campaignName: assignment.campaignName || 'Unknown Campaign',
        taskLabel: assignment.taskName || 'Unknown Task',
        mediaFormatName: assignment.formatName || '—',
        expectedQty: assignment.expectedQuantity || 0,
        deliveredQty: assignment.deliveredQuantity,
        comments: assignment.comments
      });
    }
  }
  
  // Log ambiguous cases in development
  if (enableDevWarnings && ambiguousCases.length > 0) {
    // eslint-disable-next-line no-console
    console.warn('[signoff-grouping] ambiguous pack grouping, falling back to flat', { 
      count: ambiguousCases.length,
      sample: ambiguousCases[0] 
    });
  }
  
  // Convert pack groups to final format, preserving order from sorted input
  const groups: PackGroup[] = Array.from(packGroupMap.entries()).map(([groupKey, groupData]) => ({
    type: 'pack' as const,
    groupKey,
    campaignName: groupData.campaignName,
    taskLabel: groupData.taskLabel,
    items: groupData.items.sort((a, b) => a.mediaFormatName.localeCompare(b.mediaFormatName))
  }));
  
  // Groups and singles are already in operator order from the sorted input
  // No additional sorting needed
  
  return {
    groups,
    singles
  };
}
